import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sourceService, type Source, type SourceType, type SourceTypeConfig, type DataLoad, type CatalogResponse, type ConfigFieldOption, type ListRecordsResponse, type SecureConfigLinkDetails, type FlatFileUploadLinkDetails, type ShareableAuthLinkDetails } from '../services/source.service';
import { ApiError } from '../services/api.service';

export function useSource(sourceId: string | null | undefined) {
  return useQuery<Source>({
    queryKey: ['source', sourceId],
    queryFn: () => {
      if (!sourceId) {
        return Promise.reject(new Error('Source ID is required'));
      }
      return sourceService.findById(sourceId);
    },
    enabled: !!sourceId && sourceId !== 'flat_files' && sourceId !== 'snowflake',
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404 errors - the source doesn't exist
      if (error instanceof ApiError && error.code === 404) {
        return false;
      }
      // For other errors, retry up to 3 times
      return failureCount < 3;
    },
    refetchOnWindowFocus: false, // Don't refetch on window focus for non-existent sources
  });
}

export function useSources(options?: {
  page?: number;
  pageSize?: number;
  filters?: Record<string, any>;
  enabled?: boolean;
}) {
  const {
    page = 1,
    pageSize = 50,
    filters = {},
    enabled = true,
  } = options || {};

  return useQuery<ListRecordsResponse<Source>>({
    queryKey: ['sources', page, pageSize, filters],
    queryFn: () => sourceService.find({ page, pageSize, filters }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useSourceTypes(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {};

  return useQuery<SourceType[]>({
    queryKey: ['sourceTypes'],
    queryFn: () => sourceService.getSourceTypes(),
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes - source types rarely change
  });
}

export function useCreateSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (source: Partial<Source>) => sourceService.create(source),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] });
    },
  });
}

export function useUpdateSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (source: Partial<Source>) => sourceService.save(source),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sources'] });
      queryClient.invalidateQueries({ queryKey: ['source', data.id] });
    },
  });
}

export function useDeleteSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sourceService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] });
    },
  });
}

export function useSourceTypeConfig(sourceType: string | undefined) {
  return useQuery<SourceTypeConfig>({
    queryKey: ['sourceTypeConfig', sourceType],
    queryFn: () => sourceService.getSourceTypeConfig(sourceType!),
    enabled: !!sourceType,
    staleTime: 30 * 60 * 1000,
  });
}

export function useDataLoads(sourceId: string | undefined) {
  return useQuery<DataLoad[]>({
    queryKey: ['dataLoads', sourceId],
    queryFn: () => sourceService.getDataLoads(sourceId!),
    enabled: !!sourceId,
    staleTime: 30 * 1000,
    // While a load is active, poll so live progress + the QUEUED->SYNCING flip
    // update on their own. Also poll before the first result lands, so a page
    // opened on a QUEUED sync reliably arms the interval (a function
    // refetchInterval that returns false while data is undefined can fail to
    // re-arm once data arrives).
    refetchInterval: (query) => {
      const loads = query.state.data;
      if (!loads) return 10000;
      return loads.some((l) => l.status === 'QUEUED' || l.status === 'SYNCING')
        ? 10000
        : false;
    },
    // Keep polling even when the tab isn't focused, so a sync you left running
    // in a background tab still updates.
    refetchIntervalInBackground: true,
  });
}

export function useSourceStreams(sourceId: string | undefined, enabled: boolean = true) {
  return useQuery<string[]>({
    queryKey: ['sourceStreams', sourceId],
    queryFn: () => sourceService.getStreams(sourceId!),
    enabled: enabled && !!sourceId,
    staleTime: 60 * 1000,
  });
}

export function useCatalog(sourceId: string | undefined, enabled: boolean = true) {
  return useQuery<CatalogResponse>({
    queryKey: ['sourceCatalog', sourceId],
    queryFn: () => sourceService.getCatalog(sourceId!),
    enabled: enabled && !!sourceId,
    staleTime: 60 * 1000,
  });
}

export function useConfigFieldOptions(
  sourceId: string | undefined,
  fieldName: string | undefined,
  enabled: boolean = true,
) {
  return useQuery<ConfigFieldOption[]>({
    queryKey: ['sourceConfigOptions', sourceId, fieldName],
    queryFn: () => sourceService.getConfigFieldOptions(sourceId!, fieldName!),
    enabled: enabled && !!sourceId && !!fieldName,
    staleTime: 60 * 1000,
  });
}

export function useSecureConfigLink(code: string | undefined) {
  return useQuery<SecureConfigLinkDetails>({
    queryKey: ['secureConfigLink', code],
    queryFn: () => sourceService.getSecureConfigLink(code!),
    enabled: !!code,
    retry: false,
  });
}

export function useShareableLinkBranding(code: string | undefined) {
  return useQuery({
    queryKey: ['shareableLinkBranding', code],
    queryFn: () => sourceService.getShareableLinkBranding(code!),
    enabled: !!code,
    retry: false,
  });
}

export function useFlatFileUploadLink(code: string | undefined) {
  return useQuery<FlatFileUploadLinkDetails>({
    queryKey: ['flatFileUploadLink', code],
    queryFn: () => sourceService.getFlatFileUploadLink(code!),
    enabled: !!code,
    retry: false,
  });
}

export function useShareableAuthLink(code: string | undefined) {
  return useQuery<ShareableAuthLinkDetails>({
    queryKey: ['shareableAuthLink', code],
    queryFn: () => sourceService.getShareableAuthLink(code!),
    enabled: !!code,
    retry: false,
  });
}
